import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import {
  IconArrowLeft,
  IconCircleCheck,
  IconCircleDashed,
  IconClock,
  IconPencil,
  IconShieldCheck,
  IconTrophy,
} from "@tabler/icons-react"

interface User {
  id: number
  name: string
  avatar_url: string | null
  seniority_title?: string | null
}

interface Review {
  id: number
  reviewer_type: string
  reviewer_name: string
  reviewer_email: string
  status: string
  score: number | null
  submitted_at: string | null
}

interface Cycle {
  id: number
  display_name: string
  status: string
  cycle_period: string
  year: number
  start_date: string | null
  end_date: string | null
  overall_score: number | null
  promoted: boolean
  promotion_notes: string | null
  user: User
  supervised_by: { id: number; name: string } | null
}

interface Props {
  [key: string]: unknown
  cycle: Cycle
  reviews: Review[]
  promotion_eligible: boolean
  can_finalize: boolean
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "completed":
      return "default"
    case "in_progress":
    case "reviews_completed":
    case "admin_review":
      return "secondary"
    case "cancelled":
      return "destructive"
    default:
      return "outline"
  }
}

function statusLabel(status: string) {
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

function reviewStatusIcon(status: string) {
  switch (status) {
    case "completed":
      return <IconCircleCheck className="h-4 w-4 text-green-600" />
    case "in_progress":
      return <IconClock className="h-4 w-4 text-yellow-600" />
    default:
      return <IconCircleDashed className="h-4 w-4 text-muted-foreground" />
  }
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export default function AdminPerformanceReviewCycleShow() {
  const { cycle, reviews, promotion_eligible, can_finalize } = usePage<Props>().props

  function handleFinalize() {
    if (confirm("Finalize this review cycle? This will mark it as completed.")) {
      router.post(`/admin/performance_review_cycles/${cycle.id}/finalize_review`)
    }
  }

  function handlePromote() {
    if (confirm(`Promote ${cycle.user.name}? This will advance them to the next seniority level.`)) {
      router.post(`/admin/performance_review_cycles/${cycle.id}/promote_employee`)
    }
  }

  return (
    <>
      <Head title={`Admin - ${cycle.display_name} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin">Admin</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/performance_review_cycles">Performance Review Cycles</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{cycle.display_name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/admin/performance_review_cycles">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold">{cycle.display_name}</h1>
                <Badge variant={statusVariant(cycle.status)}>{statusLabel(cycle.status)}</Badge>
                {cycle.promoted && (
                  <Badge variant="default" className="bg-green-600">
                    <IconTrophy className="mr-1 h-3 w-3" />
                    Promoted
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground text-sm">
                {cycle.user.name}
                {cycle.user.seniority_title ? ` - ${cycle.user.seniority_title}` : ""}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={`/admin/performance_review_cycles/${cycle.id}/edit`}>
                <IconPencil className="mr-1 h-4 w-4" />
                Edit
              </a>
            </Button>
            {can_finalize && (
              <Button size="sm" onClick={handleFinalize}>
                <IconShieldCheck className="mr-1 h-4 w-4" />
                Finalize Review
              </Button>
            )}
            {promotion_eligible && !cycle.promoted && (
              <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700" onClick={handlePromote}>
                <IconTrophy className="mr-1 h-4 w-4" />
                Promote Employee
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Cycle Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Period</span>
                <span>
                  {cycle.cycle_period.charAt(0).toUpperCase() + cycle.cycle_period.slice(1)} {cycle.year}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Start Date</span>
                <span>{formatDate(cycle.start_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">End Date</span>
                <span>{formatDate(cycle.end_date)}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Employee Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Name</span>
                <span>{cycle.user.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Supervisor</span>
                <span>{cycle.supervised_by?.name || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Level</span>
                <span>{cycle.user.seniority_title || "—"}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Review Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reviews</span>
                <span>
                  {reviews.filter((r) => r.status === "completed").length}/{reviews.length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Overall Score</span>
                <span className="font-semibold">{cycle.overall_score ? cycle.overall_score.toFixed(1) : "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Promotion Eligible</span>
                <span>
                  {promotion_eligible ? (
                    <Badge variant="default" className="bg-green-600 text-xs">
                      Yes
                    </Badge>
                  ) : (
                    "No"
                  )}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {reviews.length > 0 && (
          <div>
            <h2 className="mb-3 text-lg font-semibold">Reviews</h2>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>Type</TableHead>
                  <TableHead>Reviewer</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review) => (
                  <TableRow key={review.id}>
                    <TableCell>{reviewStatusIcon(review.status)}</TableCell>
                    <TableCell className="capitalize font-medium">{review.reviewer_type}</TableCell>
                    <TableCell>{review.reviewer_name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{review.reviewer_email}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(review.status)}>{statusLabel(review.status)}</Badge>
                    </TableCell>
                    <TableCell>
                      {review.score ? (
                        <span className="font-medium">{review.score.toFixed(1)}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{formatDate(review.submitted_at)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" asChild>
                        <a href={`/performance_review_cycles/${cycle.id}/performance_reviews/${review.id}`}>View</a>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {cycle.promotion_notes && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Promotion Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{cycle.promotion_notes}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}

AdminPerformanceReviewCycleShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
